import { fetchWithAuth } from '$lib/utils/http/httpUtil';
import { extractErrorMessage } from '$lib/utils/error/errorUtils';
import { showToast } from '$lib/helpers/toastrHelper';
import { writable } from 'svelte/store';
import {
  type CreditCardType,
  type DirectFeedFolder,
  type GetDirectFeedConfigsResponse,
  type GetDirectFeedFeedFilesResponse,
  type ToggleProcessingRequest,
  type ValidateFileRequest,
  type ValidateVisaFirstFileResponse,
  type DirectFeedConfig,
  type FilenameWhitelist
} from '$lib/models/ccr/types';

interface PendingDelete {
  folder: DirectFeedFolder;
  filename: string;
}

interface StarterDataValidationResult {
  filename: string;
  isValid: boolean;
  message: string;
  missingDataWarnings: string[];
}

interface CreditCardSectionState {
  localPrefixes: FilenameWhitelist[];
  localProcessingEnabled: boolean;
  pendingDeletes: PendingDelete[];
  serverPrefixes: FilenameWhitelist[];
  serverProcessingEnabled: boolean;
  serverPendingDeletes: PendingDelete[];
  starterDataValidationResult: StarterDataValidationResult | null;
}

function getInitialCreditCardState(): CreditCardSectionState {
  return {
    localPrefixes: [],
    localProcessingEnabled: false,
    pendingDeletes: [],
    serverPrefixes: [],
    serverProcessingEnabled: false,
    serverPendingDeletes: [],
    starterDataValidationResult: null
  };
}

export const creditCardSectionState = writable<Map<CreditCardType, CreditCardSectionState>>(new Map());

export function initCreditCardState(cardType: CreditCardType): void {
  creditCardSectionState.update(map => {
    if (!map.has(cardType)) {
      map.set(cardType, getInitialCreditCardState());
    }
    return new Map(map);
  });
}

export function setLocalPrefixes(cardType: CreditCardType, prefixes: FilenameWhitelist[]): void {
  creditCardSectionState.update(map => {
    const current = map.get(cardType) || getInitialCreditCardState();
    map.set(cardType, { ...current, localPrefixes: prefixes });
    return new Map(map);
  });
}

export function setLocalProcessingEnabled(cardType: CreditCardType, enabled: boolean): void {
  creditCardSectionState.update(map => {
    const current = map.get(cardType) || getInitialCreditCardState();
    map.set(cardType, { ...current, localProcessingEnabled: enabled });
    return new Map(map);
  });
}

export function addPendingDelete(cardType: CreditCardType, folder: DirectFeedFolder, filename: string): void {
  creditCardSectionState.update(map => {
    const current = map.get(cardType) || getInitialCreditCardState();
    map.set(cardType, {
      ...current,
      pendingDeletes: [...current.pendingDeletes, { folder, filename }]
    });
    return new Map(map);
  });
}

export function setStarterDataValidationResult(
  cardType: CreditCardType,
  result: StarterDataValidationResult | null
): void {
  creditCardSectionState.update(map => {
    const current = map.get(cardType) || getInitialCreditCardState();
    map.set(cardType, { ...current, starterDataValidationResult: result });
    return new Map(map);
  });
}

export function initFromServerConfig(
  cardType: CreditCardType,
  prefixes: FilenameWhitelist[],
  processingEnabled: boolean
): void {
  creditCardSectionState.update(map => {
    map.set(cardType, {
      localPrefixes: [...prefixes],
      serverPrefixes: [...prefixes],
      localProcessingEnabled: processingEnabled,
      serverProcessingEnabled: processingEnabled,
      pendingDeletes: [],
      serverPendingDeletes: [],
      starterDataValidationResult: null
    });
    return new Map(map);
  });
}

export function syncToServer(cardType: CreditCardType): void {
  creditCardSectionState.update(map => {
    const current = map.get(cardType) || getInitialCreditCardState();
    map.set(cardType, {
      ...current,
      serverPrefixes: [...current.localPrefixes],
      serverProcessingEnabled: current.localProcessingEnabled,
      serverPendingDeletes: [],
      pendingDeletes: []
    });
    return new Map(map);
  });
}

export function rollbackFromServer(cardType: CreditCardType): void {
  creditCardSectionState.update(map => {
    const current = map.get(cardType) || getInitialCreditCardState();
    map.set(cardType, {
      ...current,
      localPrefixes: [...current.serverPrefixes],
      localProcessingEnabled: current.serverProcessingEnabled,
      pendingDeletes: [...current.serverPendingDeletes]
    });
    return new Map(map);
  });
}

function createDirectFeedStore() {
  const baseUrl = '/api/ccr';

  return {
    async getConfigs(clientId: number): Promise<DirectFeedConfig[]> {
      const response = await fetchWithAuth(
        `${baseUrl}/direct-feed/configs?clientId=${clientId}`
      );

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, 'Failed to load CCR configurations');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }

      const result = await response.json() as { success: boolean; data: GetDirectFeedConfigsResponse };
      const configs = result.data?.configs || [];

      return configs.map((config: DirectFeedConfig) => ({
        ...config,
        filenameWhitelist: config.filenameWhitelist || []
      }));
    },

    async getFiles(clientId: number, creditCardType: CreditCardType): Promise<GetDirectFeedFeedFilesResponse> {
      const response = await fetchWithAuth(
        `${baseUrl}/direct-feed/files?clientId=${clientId}&creditCardType=${creditCardType}`
      );

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, 'Failed to load CCR feed files');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }

      const result = await response.json() as { success: boolean; data: GetDirectFeedFeedFilesResponse };
      return result.data || {
        unprocessedFiles: [],
        erroredFiles: [],
        processedFiles: [],
        filenamePrefixSuggestion: ''
      };
    },

    async toggleProcessing(request: ToggleProcessingRequest): Promise<void> {
      const response = await fetchWithAuth(
        `${baseUrl}/direct-feed/toggle-processing`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(request)
        }
      );

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, 'Failed to toggle processing');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }
    },

    async validateVisaFirstFile(request: ValidateFileRequest): Promise<void> {
      const response = await fetchWithAuth(
        `${baseUrl}/direct-feed/validate-visa-first-file`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(request)
        }
      );

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, 'Failed to validate VISA First file');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }

      const result = await response.json() as { success: boolean; data: ValidateVisaFirstFileResponse };
      const data = result.data || {
        isValidFirstFile: false,
        message: 'Validation failed',
        missingDataWarnings: []
      };

      setStarterDataValidationResult(request.creditCardType, {
        filename: request.filename,
        isValid: data.isValidFirstFile,
        message: data.message,
        missingDataWarnings: data.missingDataWarnings || []
      });
    },

    async deleteFile(
      clientId: number,
      creditCardType: CreditCardType,
      folder: DirectFeedFolder,
      filename: string
    ): Promise<void> {
      const encodedFilename = encodeURIComponent(filename);
      const response = await fetchWithAuth(
        `${baseUrl}/direct-feed/file?clientId=${clientId}&creditCardType=${creditCardType}&folder=${folder}&filename=${encodedFilename}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, 'Failed to delete file');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }
    }
  };
}

export const directFeedStore = createDirectFeedStore();